import React, { useEffect, useState } from 'react';
import { useColyseusMessages } from '../hooks/useColyseusMessages';
import { COLORS, FONTS } from '../theme';

/**
 * DeathOverlay — minimal "RESPAWNING in N…" countdown shown when the
 * local player dies in PvP.
 *
 * MVP scope: full-screen tinted overlay + countdown text. No killfeed,
 * no kill-cam, no "you were killed by X" header — those land in
 * Phase 2 polish. Auto-hides on `playerRespawned` for the local sid.
 *
 * Subscribes directly to room messages via `window.nm.getRoom()` (same
 * pattern as LatencyOverlay reading window.__HELIX_MP_DIAGNOSTICS__).
 * Polls room reference every 500ms in case the room is rebound on
 * reconnect.
 */

interface DeathState {
  killerName: string;
  respawnAtMs: number; // wallclock ms when respawn fires
}

function getMySid(): string | null {
  return (globalThis as any).nm?.getRoom?.()?.sessionId ?? null;
}

export const DeathOverlay: React.FC = () => {
  const [death, setDeath] = useState<DeathState | null>(null);
  const [now, setNow] = useState<number>(Date.now());

  useColyseusMessages({
    playerDied: (msg: any) => {
      if (msg?.victimSid !== getMySid()) return;
      setDeath({
        killerName: msg?.killerName ?? 'Unknown',
        respawnAtMs: Date.now() + (msg?.respawnDelayMs ?? 3000),
      });
    },
    playerRespawned: (msg: any) => {
      if (msg?.victimSid !== getMySid()) return;
      setDeath(null);
    },
  });

  // Countdown ticker — runs only while the overlay shows (B7: an always-on
  // 10 Hz ticker re-rendered this hidden component all game long). The clock
  // lives in `death.respawnAtMs`, so a restarted ticker resumes in place.
  useEffect(() => {
    if (!death) return undefined;
    setNow(Date.now());
    const tickInterval = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(tickInterval);
  }, [death]);

  // Grayscale CSS filter on game canvas during the ghost window.
  useEffect(() => {
    if (!death) return;
    const target =
      document.getElementById('game-root') ??
      document.querySelector('canvas') ??
      document.body;
    const prevFilter = (target as HTMLElement).style.filter;
    (target as HTMLElement).style.filter = 'grayscale(0.85) brightness(0.7)';
    (target as HTMLElement).style.transition = 'filter 200ms ease-in-out';
    return () => {
      (target as HTMLElement).style.filter = prevFilter;
    };
  }, [death]);

  if (!death) return null;

  const remainingMs = Math.max(0, death.respawnAtMs - now);
  const remainingSec = (remainingMs / 1000).toFixed(1);

  return (
    <div
      data-testid="death-overlay"
      data-respawn-remaining-ms={remainingMs}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'radial-gradient(ellipse at center, rgba(35, 10, 10, 0.45) 0%, rgba(5, 5, 8, 0.88) 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        pointerEvents: 'none',
        fontFamily: 'Inter, sans-serif',
        color: '#fff',
        animation: 'fadeIn 0.3s ease-out',
      }}
    >
      <div
        style={{
          fontFamily: FONTS.display,
          fontSize: 52,
          fontWeight: 700,
          letterSpacing: '0.15em',
          color: COLORS.danger,
          textShadow: '0 0 30px rgba(231, 76, 60, 0.5), 0 2px 12px rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <span>💀</span>
        <span>YOU DIED</span>
      </div>
      <div
        style={{
          marginTop: 14,
          fontSize: 18,
          opacity: 0.85,
          letterSpacing: '0.04em',
          textShadow: '0 2px 6px rgba(0, 0, 0, 0.8)',
        }}
      >
        Killed by <span style={{ color: COLORS.textAccent, fontWeight: 600 }}>{death.killerName}</span>
      </div>
      <div
        style={{
          marginTop: 28,
          fontSize: 16,
          fontWeight: 500,
          letterSpacing: '0.06em',
          color: 'rgba(255, 255, 255, 0.8)',
          textShadow: '0 2px 6px rgba(0, 0, 0, 0.8)',
        }}
      >
        Respawning in <span style={{ color: COLORS.info, fontWeight: 700, fontSize: 18 }}>{remainingSec}s</span>
      </div>
    </div>
  );
};
